import { type CoreEventBus } from '@databases/core-event-bus';
import type { FlagsManager } from '@databases/flags-types';
import type { HighlightsController } from '@databases/highlights';
import type { PanelManager } from '@databases/panel-types';
import type { DatabasePermissionsManager } from '@databases/permissions';
import type { PluginsChecker } from '@databases/plugins-types';
import type { DatabaseSdkContract } from '@databases/sdk-types';
import type {
  SelectionsActions,
  SelectionsApiContract,
  SelectionsReader
} from '@databases/selections-api-types';
import { type DatabaseFieldType } from '@databases/types/database';
import type { ViewLayoutRegistry } from '@databases/view-layout-modules';
import { type IntlShape } from 'react-intl-next';

import { type PreviewPanelAFMBridge } from '@canvas/databases-bridge-api';
import type { SmartLinkClientContract } from '@canvas/smart-link-client/databases';

import { ServiceContainer } from './service-container.ts';
import { type FactoryFunction } from './types.ts';

/**
 * The shared presence store, as seen by cross-package consumers (the telepointer
 * feed and analytics). Structurally typed here — participant params as `unknown`
 * — to avoid a project-reference cycle: the concrete `PresenceState` lives in
 * `@databases/presence`, which transitively depends on this package (same
 * cycle-break as `FieldTypeRegistry`). In-package consumers (`usePresence`, the
 * ping bridge) use the concrete store directly.
 */
export interface PresenceStateService {
  addParticipant(grouping: unknown, participant: unknown): void;
  removeParticipant(participant: unknown): void;
  readonly accountIdToParticipantsMap: ReadonlyMap<string, string>;
}

export type ContainerServices = {
  DatabaseSdk: DatabaseSdkContract;
  EventBus: CoreEventBus;
  FlagsManager: FlagsManager;
  Intl: IntlShape;
  PanelManager: PanelManager;
  PermissionsManager: DatabasePermissionsManager;
  PluginsManager: PluginsChecker;
  PreviewPanelAFMBridge: PreviewPanelAFMBridge;
  SelectionsActions: SelectionsActions;
  SelectionsApi: SelectionsApiContract;
  SelectionsReader: SelectionsReader;
  SmartLinkClient: SmartLinkClientContract;
  TableHighlightsController: HighlightsController;
  PresenceState: PresenceStateService;
  ViewLayoutRegistry: ViewLayoutRegistry;
  /**
   * Map of DatabaseFieldType → FieldType implementation.
   *
   * This is a temporary solution to break the circular dependency between
   * utils/field-types.ts and utils/converted-value-helpers.ts. The registry
   * is populated at app bootstrap and accessed via getFieldTypeRegistry() in
   * @databases/utils. See field-type-registry.ts for the builder.
   *
   * Typed as Record<DatabaseFieldType, unknown> because the full FieldType
   * interface lives in @databases/utils which service-container cannot depend on.
   * Cast to the concrete FieldType at databases/packages/utils/src/database/field-types.ts.
   */
  FieldTypeRegistry: Record<DatabaseFieldType, unknown>;
};

export type ContainerOverrideOptions = {
  [
    K in keyof Omit<
      ContainerServices,
      | 'DatabaseSdk'
      | 'EventBus'
      | 'PermissionsManager'
      | 'SelectionsActions'
      | 'SelectionsReader'
      | 'SelectionsApi'
      | 'SmartLinkClient'
      | 'TableHighlightsController'
      | 'PresenceState'
      | 'ViewLayoutRegistry'
      | 'FieldTypeRegistry'
    >
  ]: FactoryFunction<ContainerServices, ContainerServices[K]>;
};

export const container = new ServiceContainer<ContainerServices>();

/**
 * Simple utility for resolving services from the container.
 */
export function getService<K extends keyof ContainerServices>(
  key: K
): ContainerServices[K] {
  return container.resolve(key);
}
